import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Dialog } from './ui/dialog'
import { Button } from './ui/button'
import { useT } from '@/i18n'
import type { ModerationLabel } from '@/lib/adapters'

const LABELS: ModerationLabel[] = ['scam', 'fake', 'fraud', 'spam']

/**
 * Мітка модерації (scam/fake/fraud/spam) — окрема від бану/заморозки дія:
 * не блокує акаунт/чат, лише фіксує About на канонічний текст і (для
 * каналу/бота) заморожує власника. Спільний діалог для UsersScreen і
 * GroupsScreen.
 */
export function LabelDialog({
  open,
  onClose,
  title,
  currentLabel,
  onSelect,
  onClear,
}: {
  open: boolean
  onClose: () => void
  title: string
  currentLabel?: string | null
  onSelect: (label: ModerationLabel) => Promise<void>
  onClear: () => Promise<void>
}) {
  const t = useT()
  const [busy, setBusy] = useState<string | null>(null)

  if (!open) return null

  const labelText: Record<ModerationLabel, string> = {
    scam: t.users.labelScam,
    fake: t.users.labelFake,
    fraud: t.users.labelFraud,
    spam: t.users.labelSpam,
  }

  const run = async (fn: () => Promise<void>, key: string) => {
    setBusy(key)
    try {
      await fn()
      onClose()
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={t.users.labelDesc}
      icon={<AlertTriangle size={16} className="text-warning" />}
      width="sm"
    >
      <div className="space-y-1.5">
        {LABELS.map((l) => (
          <Button
            key={l}
            variant={currentLabel === l ? 'warning' : 'default'}
            className="w-full justify-start"
            disabled={busy !== null}
            onClick={() => void run(() => onSelect(l), l)}
          >
            {busy === l ? t.common.saving : labelText[l]}
          </Button>
        ))}
        {currentLabel && (
          <Button
            variant="danger"
            className="w-full justify-start"
            disabled={busy !== null}
            onClick={() => void run(() => onClear(), 'clear')}
          >
            {busy === 'clear' ? t.common.saving : t.users.labelClear}
          </Button>
        )}
      </div>
    </Dialog>
  )
}
